import React, { cloneElement, isValidElement, useId } from 'react';

// Campo de formulario compartido de VLS (auditoría de UI 2026-10-20, Lote 0):
// rótulo + control + ayuda + error, con el rótulo ASOCIADO al control. Hoy hay
// cientos de <label> hermanos sin htmlFor (CRM ~69, vls-ti 68, visitas 27,
// rrhh 26, comisiones 24): el lector de pantalla no nombra el campo y tocar el
// rótulo no enfoca nada. Generaliza el Campo de EquipoForm del hub.
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:
//     <div>
//       <label className="text-sm font-medium">Monto</label>
//       <input value={m} onChange={…} className="…" />
//       {err && <p className="text-xs text-error">{err}</p>}
//     </div>
//   DESPUÉS:
//     <Campo label="Monto" error={err} ayuda="En colones, sin decimales.">
//       <input value={m} onChange={…} className="…" />
//     </Campo>
//   El control (un solo elemento hijo: input, textarea, el Select o el
//   DatePicker compartidos) recibe solo id, aria-describedby, aria-invalid y
//   required; si ya trae su propio id, se respeta y el rótulo apunta a ese.
//   Para controles que no aceptan esas props, usar una función:
//     <Campo label="Fecha">{(p) => <MiControl inputId={p.id} {...p} />}</Campo>
//
// Props: label (obligatorio), children (elemento o función que recibe
// { id, 'aria-describedby', 'aria-invalid', required }), id (si no viene,
// se genera), ayuda (texto bajo el control), error (texto o nodo; marca
// aria-invalid y se anuncia con role="alert"), requerido (asterisco visual y
// required en el control), opcional (agrega "(opcional)" al rótulo),
// className (solo layout), labelClassName (para ocultar el rótulo a la vista
// con sr-only cuando el diseño no lo muestra: sigue asociado).
// Sin dependencias; colores por var(--color-*) con respaldo, medidas en px.

const C = {
  fg: 'var(--color-foreground, #1C1917)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  error: 'var(--color-error, #DC2626)',
};

export default function Campo({
  label, children, id: idProp, ayuda, error, requerido = false, opcional = false,
  className = '', labelClassName = '', 'data-testid': testId,
}) {
  const generado = useId();
  const hijoId = isValidElement(children) ? children.props.id : undefined;
  const id = hijoId || idProp || `campo-${generado.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const idAyuda = ayuda ? `${id}-ayuda` : undefined;
  const idError = error ? `${id}-error` : undefined;

  const propsControl = {
    id,
    'aria-describedby': [idAyuda, idError].filter(Boolean).join(' ') || undefined,
    'aria-invalid': error ? true : undefined,
    required: requerido || undefined,
  };

  let control;
  if (typeof children === 'function') {
    control = children(propsControl);
  } else if (isValidElement(children)) {
    const previo = children.props['aria-describedby'];
    control = cloneElement(children, {
      ...propsControl,
      'aria-describedby': [previo, propsControl['aria-describedby']].filter(Boolean).join(' ') || undefined,
      required: children.props.required ?? propsControl.required,
    });
  } else {
    control = children;
  }

  return (
    <div className={className} data-vls-campo="" data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} className={`text-sm font-medium ${labelClassName}`} style={{ color: C.fg }}>
        {label}
        {requerido && <span aria-hidden="true" style={{ color: C.error, marginLeft: 2 }}>*</span>}
        {opcional && !requerido && <span style={{ color: C.mutedFg, fontWeight: 400, marginLeft: 4 }}>(opcional)</span>}
      </label>
      {control}
      {ayuda && (
        <p id={idAyuda} className="text-sm" style={{ margin: 0, color: C.mutedFg }}>{ayuda}</p>
      )}
      {error && (
        <p id={idError} role="alert" className="text-sm" style={{ margin: 0, color: C.error }}>{error}</p>
      )}
    </div>
  );
}
